import { useCallback, useEffect, useRef, useState } from "react";

type SeekDirection = "left" | "right" | null;

interface UseVideoGesturesProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  onTap: () => void;
  onDoubleTap?: () => void;
  onLongPressStart?: () => void;
  onLongPressEnd?: () => void;
  onSeek?: (from: number, to: number) => void;
}

export const useVideoGestures = ({
  videoRef,
  containerRef,
  onTap,
  onDoubleTap,
  onLongPressStart,
  onLongPressEnd,
  onSeek,
}: UseVideoGesturesProps) => {
  const [seekAnimation, setSeekAnimation] = useState<SeekDirection>(null);
  const lastTapTime = useRef(0);
  const lastTapPosition = useRef({ x: 0, y: 0 });
  const isLongPressing = useRef(false);
  const longPressTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const seekAnimationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTap = useCallback(
    (clientX: number, clientY: number) => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const tapX = clientX - rect.left;
      const tapY = clientY - rect.top;
      const now = Date.now();

      const isTapOnControls = tapY > rect.height * 0.7;
      if (isTapOnControls) {
        onTap();
        return;
      }

      const isDoubleTap =
        now - lastTapTime.current < 300 &&
        Math.abs(tapX - lastTapPosition.current.x) < 50 &&
        Math.abs(tapY - lastTapPosition.current.y) < 50;

      if (isDoubleTap) {
        onDoubleTap?.();
        const isLeftSide = tapX < rect.width / 2;
        const video = videoRef.current;
        if (video) {
          const previousTime = video.currentTime;
          let nextTime: number;
          if (isLeftSide) {
            nextTime = Math.max(0, previousTime - 10);
            video.currentTime = nextTime;
            setSeekAnimation("left");
          } else {
            nextTime = Math.min(video.duration, previousTime + 10);
            video.currentTime = nextTime;
            setSeekAnimation("right");
          }
          if (typeof onSeek === "function") {
            if (Math.abs(nextTime - previousTime) >= 1) {
              onSeek(previousTime, nextTime);
            }
          }
          if (seekAnimationTimeoutRef.current) {
            clearTimeout(seekAnimationTimeoutRef.current);
          }
          seekAnimationTimeoutRef.current = setTimeout(() => {
            setSeekAnimation(null);
          }, 600);
        }
      } else {
        onTap();
      }

      lastTapTime.current = now;
      lastTapPosition.current = { x: tapX, y: tapY };
    },
    [containerRef, onDoubleTap, onSeek, onTap, videoRef]
  );

  const handleTouchStart = useCallback(
    (event: React.TouchEvent) => {
      event.stopPropagation();
      const touch = event.touches[0];
      if (!touch || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const touchY = touch.clientY - rect.top;
      if (touchY > rect.height * 0.7) return;

      isLongPressing.current = true;
      longPressTimeoutRef.current = setTimeout(() => {
        if (isLongPressing.current) {
          onLongPressStart?.();
        }
      }, 500);
    },
    [containerRef, onLongPressStart]
  );

  const handleTouchEnd = useCallback(() => {
    isLongPressing.current = false;
    if (longPressTimeoutRef.current) {
      clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
    }
    onLongPressEnd?.();
  }, [onLongPressEnd]);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (event.button !== 0) return;
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const mouseY = event.clientY - rect.top;
      if (mouseY > rect.height * 0.7) return;

      isLongPressing.current = true;
      longPressTimeoutRef.current = setTimeout(() => {
        if (isLongPressing.current) {
          onLongPressStart?.();
        }
      }, 500);
    },
    [containerRef, onLongPressStart]
  );

  const handleMouseUp = useCallback(() => {
    isLongPressing.current = false;
    if (longPressTimeoutRef.current) {
      clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
    }
    onLongPressEnd?.();
  }, [onLongPressEnd]);

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      handleTap(event.clientX, event.clientY);
    },
    [handleTap]
  );

  useEffect(() => {
    return () => {
      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
      if (seekAnimationTimeoutRef.current) {
        clearTimeout(seekAnimationTimeoutRef.current);
      }
    };
  }, []);

  return {
    seekAnimation,
    handleClick,
    handleTouchStart,
    handleTouchEnd,
    handleMouseDown,
    handleMouseUp,
  };
};
